import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  TextField,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';

const NewSpeciesTable = ({ supabase }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newSpecies, setNewSpecies] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');

  const fetchNewSpecies = async (date) => {
    try {
      setLoading(true);
      setError(null);
      
      if (!supabase) {
        throw new Error('Supabase client not configured');
      }

      const fetchAllPages = async (queryFactory) => {
        const pageSize = 1000;
        let from = 0;
        let allData = [];
        let hasMore = true;

        while (hasMore) {
          const { data, error } = await queryFactory(from, from + pageSize - 1);
          if (error) throw error;
          allData = allData.concat(data || []);
          hasMore = data && data.length === pageSize;
          from += pageSize;
        }

        return allData;
      };

      // Use explicit UTC dates to avoid timezone issues
      const startOfDay = new Date(date + 'T00:00:00.000Z');
      const endOfDay = new Date(date + 'T23:59:59.999Z');

      // Get species detected on the selected date
      const todaySpecies = await fetchAllPages((from, to) => (
        supabase
          .from('detection')
          .select(`
            species (
              common_name,
              scientific_name,
              bow_url
            )
          `)
          .gte('absolute_start_time', startOfDay.toISOString())
          .lte('absolute_start_time', endOfDay.toISOString())
          .gte('confidence', 0.1)
          .order('absolute_start_time')
          .range(from, to)
      ));

      if (!todaySpecies || todaySpecies.length === 0) {
        setNewSpecies([]);
        return;
      }

      // Get unique species from today
      const todayUniqueSpecies = [...new Set(
        todaySpecies
          .filter(d => d.species?.common_name)
          .map(d => d.species.common_name)
      )];

      // Check which of today's species were NOT detected before the selected date
      const historicalSpecies = await fetchAllPages((from, to) => (
        supabase
          .from('detection')
          .select(`
            species (
              common_name
            )
          `)
          .lt('absolute_start_time', startOfDay.toISOString())
          .gte('confidence', 0.1)
          .order('absolute_start_time')
          .range(from, to)
      ));

      const historicalSpeciesNames = new Set(
        (historicalSpecies || [])
          .filter(d => d.species?.common_name)
          .map(d => d.species.common_name)
      );

      // Find new species (detected today but not in historical data)
      const newSpeciesData = todaySpecies
        .filter(d => d.species?.common_name && !historicalSpeciesNames.has(d.species.common_name))
        .reduce((acc, d) => {
          if (!acc.find(item => item.common_name === d.species.common_name)) {
            acc.push(d.species);
          }
          return acc;
        }, [])
        .sort((a, b) => a.common_name.localeCompare(b.common_name));

      setNewSpecies(newSpeciesData);
    } catch (err) {
      console.error('Error fetching new species:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Default to previous day
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayString = yesterday.toISOString().split('T')[0];
    setSelectedDate(yesterdayString);
    
    if (supabase) {
      fetchNewSpecies(yesterdayString);
    }
  }, [supabase]);

  const handleDateChange = (event) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);
    if (newDate && supabase) {
      fetchNewSpecies(newDate);
    }
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const formatDate = (dateString) => {
    // Create date object and ensure we're working with the date as intended
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CalendarIcon color="primary" />
            <Typography variant="h5" fontWeight="medium">
              🆕 New Species Discovered
            </Typography>
            <Chip 
              label={newSpecies.length === 1 ? `${newSpecies.length} species` : `${newSpecies.length} species`}
              color={newSpecies.length > 0 ? "success" : "default"}
              size="small"
            />
          </Box>
          <IconButton onClick={handleExpandClick}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <TextField
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            size="small"
            sx={{ minWidth: 150 }}
          />
          <Typography variant="body2" color="text.secondary">
            {selectedDate && formatDate(selectedDate)}
          </Typography>
        </Box>
      </CardContent>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ pt: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error loading new species: {error}
            </Alert>
          ) : newSpecies.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No new species discovered on {formatDate(selectedDate)}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Common Name</TableCell>
                    <TableCell>Scientific Name</TableCell>
                    <TableCell align="center">More Info</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {newSpecies.map((species, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {species.common_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          {species.scientific_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {species.bow_url ? (
                          <IconButton
                            size="small"
                            component="a"
                            href={species.bow_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            color="primary"
                          >
                            🔗
                          </IconButton>
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default NewSpeciesTable;
